import React, { useMemo, useState } from 'react';
import DefaultLayout from '@/layout/DefaultLayout';
import Breadcrumb from '@/components/Breadcrumb';
import { useQueryClient, useQuery, useMutation } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import DynamicTable from '@/components/DynamicTable';
import Pagination from '@/components/Pagination';
import Spinner from '@/components/Spinner';
import { DeleteButton, EditButton } from '@/components/shared/Button';
import DeleteConfirmationModal from '@/components/shared/DeleteConfirmationModal';
import {
  QuestionResponseDto,
  QuestionResponseResponse,
  deleteQuestionResponse,
  getQuestionResponsesByProjectId,
} from '@/services/QuestionResponsesService';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { QuestionStatus } from '@/enums/QuestionStatus';

const userTimeZone = moment.tz.guess();

function QuestionResponses() {
  const { id } = useParams();
  const [pageIndex, setPageIndex] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState<string>('');
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedRow, setSelectedRow] = useState('');
  const navigate = useNavigate();

  const { t } = useTranslation();

  const ActionButtonsCell = ({ value }: any) => {
    return (
      <div>
        <DeleteButton
          onClick={() => {
            setSelectedRow(value);
            setIsModalVisible(true);
          }}
        >
          {t('QuestionResponses.deleteButton')}
        </DeleteButton>

        <EditButton
          type="button"
          onClick={() => {
            navigate(`${value}`);
          }}
        >
          {t('QuestionResponses.editButton')}
        </EditButton>
      </div>
    );
  };

  const handleCancelDelete = () => {
    setIsModalVisible(false);
  };

  const handleDeleteConfirmation = () => {
    handleDeleteQuestionResponse.mutate(selectedRow);
    setIsModalVisible(false);
  };

  const columns = useMemo(
    () => [
      {
        Header: t('QuestionResponses.actionsColumn'),
        accessor: 'id',
        Cell: ({ cell: { value } }: any) => (
          <ActionButtonsCell
            value={value}
            handleDelete={handleDeleteQuestionResponse}
          />
        ),
      },
      {
        Header: t('QuestionResponses.questionDateColumn'),
        accessor: 'questionDate',
        Cell: ({ cell: { value } }: any) => {
          const formattedValue = moment(value).format('YYYY-MM-DD'); // Customize the format as you need
          return (
            <input
              type="text"
              className="min-w-40 bg-transparent"
              value={formattedValue}
              readOnly
            />
          );
        },
      },
      {
        Header: t('QuestionResponses.questionAuthorColumn'),
        accessor: 'questionAuthor',
      },
      {
        Header: t('QuestionResponses.questionColumn'),
        accessor: 'question',
      },
      {
        Header: t('QuestionResponses.responseDateColumn'),
        accessor: 'responseDate',
        Cell: ({ cell: { value } }: any) => {
          const formattedValue = value
            ? moment(value).format('YYYY-MM-DD')
            : '';
          return (
            <input
              type="text"
              className="min-w-40 bg-transparent"
              value={formattedValue}
              readOnly
            />
          );
        },
      },
      {
        Header: t('QuestionResponses.responseColumn'),
        accessor: 'response',
      },
      {
        Header: t('QuestionResponses.responderNameColumn'),
        accessor: 'responderName',
      },
      {
        Header: t('QuestionResponses.statusColumn'),
        accessor: 'status',
        Cell: ({ cell: { value } }) =>
          t(`QuestionStatus.${QuestionStatus[value]}`),
      },
      {
        Header: t('QuestionResponses.commentsColumn'),
        accessor: 'comments',
      },
    ],
    [t]
  );

  const queryClient = useQueryClient();

  const {
    isLoading,
    isError,
    data: questionResponses,
  } = useQuery<QuestionResponseResponse | undefined>(
    ['questionResponses', pageIndex, pageSize, search, id],
    () =>
      getQuestionResponsesByProjectId({
        projectId: id!,
        page: pageIndex,
        take: pageSize,
        search: search,
      }),
    {
      refetchOnWindowFocus: false,
      staleTime: 6000,
      keepPreviousData: true,
      //   enabled: id !== null,
    }
  );

  const handleDeleteQuestionResponse = useMutation(
    async (id: string) => {
      await deleteQuestionResponse(id);
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries([
          'questionResponses',
          pageIndex,
          pageSize,
          search,
        ]);
      },
    }
  );

  const pageCount = useMemo(() => {
    return Math.ceil(
      (questionResponses?.questionResponses.length ?? 0) / pageSize
    );
  }, [pageSize, questionResponses]);

  const nextPage = () => setPageIndex(pageIndex + 1);
  const previousPage = () => setPageIndex(pageIndex - 1);
  const onPageChange = (newPageIndex: number) => setPageIndex(newPageIndex);
  const onPageSizeChange = (newPageSize: number) => {
    setPageIndex(0);
    setPageSize(newPageSize);
  };

  return (
    <DefaultLayout>
      <Breadcrumb
        pageName={t('QuestionResponses.pageName')}
        addbtn={true}
        addRouter="new"
      />
      {isLoading ? (
        <div className="flex items-center justify-center">
          <Spinner height="3rem" width="3rem" />
        </div>
      ) : (
        <div className="flex flex-col">
          <DynamicTable
            dataCount={questionResponses?.questionResponses.length ?? 0}
            data={questionResponses?.questionResponses ?? []}
            columns={columns}
            setSearch={setSearch}
            exportPdf={false}
          />
          {isModalVisible && (
            <DeleteConfirmationModal
              isOpen={isModalVisible}
              onDelete={handleDeleteConfirmation}
              onCancel={handleCancelDelete}
            />
          )}
          <Pagination
            pageIndex={pageCount === 0 ? -1 : pageIndex}
            pageCount={pageCount}
            pageSize={pageSize}
            onNextPage={nextPage}
            onPreviousPage={previousPage}
            onPageChange={onPageChange}
            onPageSizeChange={onPageSizeChange}
          />
        </div>
      )}
    </DefaultLayout>
  );
}

export default QuestionResponses;
